import { useEffect, useRef, useState } from 'react';
import { supabase } from '../lib/supabase';

// Post-password MFA gate. Rendered by App.tsx whenever the current
// session is aal1 but a verified TOTP factor exists (nextLevel = aal2).
// On successful verify, Supabase upgrades the session to aal2 and the
// onAuthStateChange listener in AuthProvider re-runs the assurance-level
// check — mfaRequired flips to false and App.tsx swaps in the dashboard.
//
// Kept as its own screen instead of stashing this state inside
// LoginPage because LoginPage unmounts the instant the aal1 session
// lands, so its MFA branch was never actually reachable in practice.
export default function MfaChallengePage() {
  const [factorId, setFactorId] = useState<string | null>(null);
  const [challengeId, setChallengeId] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const [wobbleAt, setWobbleAt] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    let cancelled = false;
    const start = async () => {
      const { data: factorsData, error: listErr } = await supabase.auth.mfa.listFactors();
      if (cancelled) return;
      if (listErr) {
        setError(listErr.message);
        return;
      }
      const verified = factorsData?.totp?.find((f) => f.status === 'verified');
      if (!verified) {
        setError('مفيش حماية إضافية مفعلة على الحساب.');
        return;
      }
      const { data: chal, error: chalErr } = await supabase.auth.mfa.challenge({
        factorId: verified.id,
      });
      if (cancelled) return;
      if (chalErr || !chal) {
        setError(chalErr?.message ?? 'مقدرناش نبدأ التحقق بخطوتين — حاول تاني');
        return;
      }
      setFactorId(verified.id);
      setChallengeId(chal.id);
    };
    void start();
    return () => {
      cancelled = true;
    };
  }, []);

  // Auto-submit the moment 6 valid digits are in — matches every
  // authenticator UX the operator has seen (Google, Slack, GitHub).
  useEffect(() => {
    if (code.length === 6 && factorId && challengeId && !busy) {
      void verify();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code, factorId, challengeId]);

  const verify = async () => {
    if (!factorId || !challengeId) return;
    const trimmed = code.replace(/\s/g, '');
    if (!/^\d{6}$/.test(trimmed)) {
      setError('اكتب الكود اللي في تطبيق المصادقة (6 أرقام)');
      return;
    }
    setError(null);
    setBusy(true);
    const { error: verErr } = await supabase.auth.mfa.verify({
      factorId,
      challengeId,
      code: trimmed,
    });
    setBusy(false);
    if (verErr) {
      setError(verErr.message === 'Invalid TOTP code entered' ? 'الكود غلط، جرب تاني' : verErr.message);
      // Wobble the deck, wipe the input, and reissue a fresh challenge —
      // a used challenge can't be verified a second time.
      setCode('');
      setWobbleAt(Date.now());
      inputRef.current?.focus();
      const { data: chal } = await supabase.auth.mfa.challenge({ factorId });
      if (chal) setChallengeId(chal.id);
    }
  };

  const cancel = async () => {
    setSigningOut(true);
    await supabase.auth.signOut().catch(() => {});
  };

  // 6 slots arranged in a subtle arc — each slot rotates a few degrees
  // outward from centre so they read as a fan of playing cards. The
  // slot the next digit will land in gets a soft brand-yellow glow.
  const digits = code.padEnd(6, ' ').split('');
  const nextIndex = code.length;

  const rotations = [-9, -5.4, -1.8, 1.8, 5.4, 9];
  // A small lateral offset per slot so the fan reads as one hand and
  // not six separate rectangles.
  const offsets = [-6, -2.4, -0.8, 0.8, 2.4, 6];

  return (
    <div className="center login-scene">
      <style>{`
        @keyframes mfa-slot-pop {
          0%   { transform: scale(1); }
          40%  { transform: scale(1.14); }
          100% { transform: scale(1); }
        }
        @keyframes mfa-slot-glow {
          0%, 100% { box-shadow: 0 6px 22px rgba(254, 200, 48, 0.25), inset 0 0 0 1px rgba(254, 200, 48, 0.55); }
          50%      { box-shadow: 0 8px 30px rgba(254, 200, 48, 0.40), inset 0 0 0 1px rgba(254, 200, 48, 0.85); }
        }
        @keyframes mfa-deck-wobble {
          0%,100% { transform: translateX(0); }
          20%     { transform: translateX(-8px); }
          40%     { transform: translateX(7px); }
          60%     { transform: translateX(-5px); }
          80%     { transform: translateX(3px); }
        }
        .mfa-deck {
          position: relative;
          display: flex;
          justify-content: center;
          align-items: flex-end;
          gap: 6px;
          padding: 24px 8px 16px;
          direction: ltr;
        }
        .mfa-deck.wobble { animation: mfa-deck-wobble 400ms ease; }
        .mfa-slot {
          width: 52px;
          height: 72px;
          border-radius: 12px;
          background: linear-gradient(180deg, #ffffff 0%, #fbfaf5 100%);
          border: 1px solid rgba(0, 144, 145, 0.22);
          box-shadow: 0 6px 14px rgba(0, 0, 0, 0.06);
          display: flex;
          align-items: center;
          justify-content: center;
          font-family: 'Titillium Web', 'Cairo', system-ui, sans-serif;
          font-weight: 800;
          font-size: 30px;
          color: #005959;
          transform-origin: 50% 100%;
          transition: transform 260ms cubic-bezier(.2,.7,.2,1),
                      box-shadow 260ms ease,
                      background 260ms ease,
                      color 260ms ease;
        }
        .mfa-slot--filled {
          background: linear-gradient(180deg, #ffffff 0%, #fff8dc 100%);
          animation: mfa-slot-pop 260ms ease-out;
        }
        .mfa-slot--next {
          animation: mfa-slot-glow 1.4s ease-in-out infinite;
          transform: translateY(-4px);
        }
        .mfa-slot--empty { color: rgba(0, 89, 89, 0.28); }
        .mfa-hidden-input {
          position: absolute;
          inset: 0;
          opacity: 0;
          cursor: pointer;
        }
        @media (prefers-reduced-motion: reduce) {
          .mfa-slot, .mfa-slot--filled, .mfa-slot--next, .mfa-deck { animation: none !important; transition: none !important; }
        }
      `}</style>

      <div className="login-aurora" aria-hidden="true">
        <span className="glow glow-a" />
        <span className="glow glow-b" />
      </div>

      <form
        className="login-card"
        onSubmit={(e) => {
          e.preventDefault();
          void verify();
        }}
      >
        <div className="login-brand">
          <div className="logo-halo" aria-hidden="true" />
          <img
            className="logo"
            src={`${import.meta.env.BASE_URL}brand/mersal-logo.png`}
            alt="Mersal"
          />
        </div>

        <div className="login-heading">
          <h1 className="login-title">التحقق بخطوتين</h1>
          <span className="login-underline" aria-hidden="true" />
        </div>

        <p style={{ textAlign: 'center', color: 'var(--text-secondary)', fontSize: 13, marginTop: -4 }}>
          افتح تطبيق المصادقة اللي ربطته على حسابك وادخل الكود
        </p>

        {error && <div className="error-inline">{error}</div>}

        {/* The deck — 6 tilted cards, each rotates from its bottom-centre
            like a hand of playing cards. Tapping the deck focuses the
            hidden input so mobile keyboards / paste flows still work
            unchanged. */}
        <div
          className={`mfa-deck ${wobbleAt ? 'wobble' : ''}`}
          key={wobbleAt}
          onClick={() => inputRef.current?.focus()}
        >
          {digits.map((d, i) => {
            const filled = i < code.length;
            const isNext = !filled && i === nextIndex && !busy;
            const cls = [
              'mfa-slot',
              filled ? 'mfa-slot--filled' : 'mfa-slot--empty',
              isNext ? 'mfa-slot--next' : '',
            ].filter(Boolean).join(' ');
            return (
              <div
                key={i}
                className={cls}
                style={{
                  transform: `translateY(${filled ? '0' : '2px'}) translateX(${offsets[i]}px) rotate(${rotations[i]}deg)`,
                  zIndex: filled ? 6 - i : 3,
                }}
              >
                {filled ? d : '•'}
              </div>
            );
          })}
          <input
            ref={inputRef}
            className="mfa-hidden-input"
            type="text"
            inputMode="numeric"
            maxLength={7}
            autoComplete="one-time-code"
            autoFocus
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            aria-label="الكود من تطبيق المصادقة (6 أرقام)"
          />
        </div>

        <div style={{ textAlign: 'center', color: 'var(--text-secondary)', fontSize: 12, marginTop: -4 }}>
          {busy ? 'جاري التحقق...' : 'اكتب الأرقام أو الصقها من التطبيق'}
        </div>

        <div style={{ textAlign: 'center', marginTop: 14 }}>
          <button
            type="button"
            onClick={() => void cancel()}
            disabled={signingOut}
            style={{
              background: 'transparent',
              border: 'none',
              color: 'var(--teal)',
              fontFamily: 'inherit',
              fontSize: 13,
              cursor: 'pointer',
              textDecoration: 'underline',
              padding: 6,
            }}
          >
            {signingOut ? 'جاري الخروج...' : 'رجوع لصفحة الدخول'}
          </button>
        </div>
      </form>
    </div>
  );
}
